import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { Controller, useForm, useWatch, type Control, type Resolver } from "react-hook-form";

import { Checkbox, SelectInput, TextInput, TextareaInput } from "@/shared/components";
import type { StepFormComponentProps, StepFormHandle } from "@/shared/stepper/types";
import { healthQuickQuestionsSchema } from "@/shared/validation/healthQuoteSchemas";
import { useHealthMasterData } from "../../hooks/useHealthMasterData";
import { defaultHealthQuickQuestionsFormValues } from "../../redux/health-quote/healthQuoteSlice";
import type { HealthMedicalConditionKey, HealthQuickQuestionsFormValues } from "../../redux/type";
import { calculateBmi } from "../../utils/bmsCalculation";

function HealthMedicalConditionsField({
  control,
  showOtherMedicalConditionDetails,
  medicalConditionsLayoutClassName,
  medicalConditionOptions,
  isReadOnly,
}: {
  control: Control<HealthQuickQuestionsFormValues>;
  showOtherMedicalConditionDetails: boolean;
  medicalConditionsLayoutClassName?: string;
  medicalConditionOptions: Array<{ key: HealthMedicalConditionKey; label: string }>;
  isReadOnly?: boolean;
}) {
  return (
    <Controller
      control={control}
      name="medicalConditions"
      render={({ field, fieldState }) => {
        const selectedConditions = Array.isArray(field.value) ? field.value : [];

        const toggleCondition = (conditionKey: HealthMedicalConditionKey) => {
          if (isReadOnly) return;

          const nextSelected = selectedConditions.includes(conditionKey)
            ? selectedConditions.filter((item) => item !== conditionKey)
            : [...selectedConditions, conditionKey];

          field.onChange(nextSelected);
        };

        return (
          <div className="space-y-3">
            <div
              className={
                medicalConditionsLayoutClassName ?? "grid grid-cols-1 gap-1 md:grid-cols-3"
              }
            >
              {medicalConditionOptions.map((option) => {
                const checked = selectedConditions.includes(option.key);

                return (
                  <label
                    key={option.key}
                    className={clsx(
                      "flex items-center gap-3 rounded-lg p-2 transition cursor-pointer",
                      checked
                        ? "border-theme-primary bg-theme-primary/5"
                        : "border-theme-border-strong bg-theme-surface"
                    )}
                  >
                    <Checkbox checked={checked} onChange={() => toggleCondition(option.key)} />
                    <span className="text-xs font-medium text-theme-text">{option.label}</span>
                  </label>
                );
              })}
            </div>

            {selectedConditions.includes("other") ? (
              <TextInput
                control={control}
                name="otherMedicalConditionDetails"
                label="Specify condition"
                placeholder="Please specify condition"
                required={showOtherMedicalConditionDetails}
              />
            ) : null}

            {fieldState.error?.message ? (
              <p className="text-xs text-theme-danger-bright">{fieldState.error.message}</p>
            ) : null}
          </div>
        );
      }}
    />
  );
}

export const HealthQuickQuestionsStep = forwardRef<
  StepFormHandle<HealthQuickQuestionsFormValues>,
  StepFormComponentProps<
    HealthQuickQuestionsFormValues,
    {
      layoutClassName?: string;
      medicalConditionsLayoutClassName?: string;
      lifestyleLayoutClassName?: string;
      placeAdditionalInformationInLifestyleGrid?: boolean;
      additionalInformationClassName?: string;
      showTitleAndDescription?: boolean;
      isReadOnly?: boolean;
    }
  >
>(function HealthQuickQuestionsStep(
  {
    defaultValues,
    layoutClassName,
    medicalConditionsLayoutClassName,
    lifestyleLayoutClassName,
    placeAdditionalInformationInLifestyleGrid = false,
    additionalInformationClassName,
    showTitleAndDescription = true,
    isReadOnly = false,
  },
  ref
) {
  const {
    healthMedicalConditionOptions,
    healthOccupationTypeOptions,
    healthWorkProfileOptions,
    healthAnnualIncomeRangeOptions,
    healthQuestionLabels,
  } = useHealthMasterData();

  const incomingDefaultValues = (defaultValues ?? {}) as Partial<HealthQuickQuestionsFormValues>;
  const resolvedDefaultValues: HealthQuickQuestionsFormValues = {
    ...defaultHealthQuickQuestionsFormValues,
    ...incomingDefaultValues,
    medicalConditions:
      incomingDefaultValues.medicalConditions ??
      defaultHealthQuickQuestionsFormValues.medicalConditions,
    additionalInformation:
      incomingDefaultValues.additionalInformation ??
      defaultHealthQuickQuestionsFormValues.additionalInformation,
    otherMedicalConditionDetails:
      incomingDefaultValues.otherMedicalConditionDetails ??
      defaultHealthQuickQuestionsFormValues.otherMedicalConditionDetails,
  };

  const form = useForm<HealthQuickQuestionsFormValues>({
    resolver: zodResolver(healthQuickQuestionsSchema) as Resolver<
      HealthQuickQuestionsFormValues,
      any,
      HealthQuickQuestionsFormValues
    >,
    defaultValues: resolvedDefaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const heightCm = useWatch({ control: form.control, name: "heightCm" });
  const weightKg = useWatch({ control: form.control, name: "weightKg" });
  const medicalConditions = useWatch({ control: form.control, name: "medicalConditions" });
  const showOtherMedicalConditionDetails = medicalConditions?.includes("other");

  useEffect(() => {
    const bmi = calculateBmi(heightCm || "", weightKg || "");
    const shouldValidateBmi = Boolean(heightCm?.trim()) && Boolean(weightKg?.trim());

    form.setValue("bmi", bmi, {
      shouldDirty: true,
      shouldValidate: shouldValidateBmi,
    });
  }, [form, heightCm, weightKg]);

  useEffect(() => {
    if (showOtherMedicalConditionDetails) return;

    if (form.getValues("otherMedicalConditionDetails")) {
      form.setValue("otherMedicalConditionDetails", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    form.clearErrors("otherMedicalConditionDetails");
  }, [form, showOtherMedicalConditionDetails]);

  useEffect(() => {
    if (!showOtherMedicalConditionDetails) return;

    void form.trigger("otherMedicalConditionDetails");
  }, [form, showOtherMedicalConditionDetails]);

  useImperativeHandle(ref, () => ({
    submit: async () => form.trigger(undefined, { shouldFocus: true }),
    getValues: () => form.getValues(),
  }));

  return (
    <fieldset disabled={isReadOnly} className={`${layoutClassName ?? "space-y-6"} border-0 p-0`}>
      <div className="space-y-3">
        {showTitleAndDescription ? (
          <div>
            <h3 className="text-sm font-semibold text-theme-text">
              {healthQuestionLabels?.medicalConditions.title}
            </h3>
            <p className="text-xs text-theme-text-soft">
              {healthQuestionLabels?.medicalConditions.description}
            </p>
          </div>
        ) : null}

        <HealthMedicalConditionsField
          control={form.control}
          showOtherMedicalConditionDetails={showOtherMedicalConditionDetails}
          medicalConditionsLayoutClassName={medicalConditionsLayoutClassName}
          medicalConditionOptions={healthMedicalConditionOptions}
          isReadOnly={isReadOnly}
        />
      </div>

      <div className="space-y-3">
        {showTitleAndDescription ? (
          <div>
            <h3 className="text-sm font-semibold text-theme-text">
              {healthQuestionLabels?.lifestyle.title}
            </h3>
            <p className="text-xs text-theme-text-soft">
              {healthQuestionLabels?.lifestyle.description}
            </p>
          </div>
        ) : null}

        <div className={lifestyleLayoutClassName ?? "grid grid-cols-1 gap-4 md:grid-cols-3"}>
          <TextInput
            control={form.control}
            name="heightCm"
            label="Height (cm)"
            placeholder="Select"
            required
          />

          <TextInput
            control={form.control}
            name="weightKg"
            label="Weight (kg)"
            placeholder="Select"
            required
          />

          <TextInput
            control={form.control}
            name="bmi"
            label="BMI (Auto)"
            placeholder="--"
            disabled
            required
          />

          <SelectInput
            control={form.control}
            name="occupationType"
            label="Occupation Type"
            placeholder="Select"
            options={healthOccupationTypeOptions}
            required
            searchable
          />

          <SelectInput
            control={form.control}
            name="workProfile"
            label="Work Profile"
            placeholder="Select"
            options={healthWorkProfileOptions}
            required
            searchable
          />

          <SelectInput
            control={form.control}
            name="annualIncomeRange"
            label="Annual Income Range"
            placeholder="Select"
            options={healthAnnualIncomeRangeOptions}
            required
            searchable
          />

          {placeAdditionalInformationInLifestyleGrid ? (
            <TextareaInput
              control={form.control}
              name="additionalInformation"
              label="Additional Information"
              placeholder="Type here (optional)"
              rows={5}
              maxLength={500}
              className={additionalInformationClassName}
            />
          ) : null}
        </div>
      </div>

      {!placeAdditionalInformationInLifestyleGrid ? (
        <div className="space-y-3">
          {showTitleAndDescription ? (
            <div>
              <h3 className="text-sm font-semibold text-theme-text">
                {healthQuestionLabels.additionalInformation.title}
              </h3>
              <p className="text-xs text-theme-text-soft">
                {healthQuestionLabels.additionalInformation.description}
              </p>
            </div>
          ) : null}

          <TextareaInput
            control={form.control}
            name="additionalInformation"
            label="Additional Information"
            placeholder="Type here (optional)"
            rows={5}
            maxLength={500}
          />
        </div>
      ) : null}
    </fieldset>
  );
});
